/**
 * Customize the food diary: rename, reorder, hide or add sections (Drinks, Vitamins...), place the
 * watch "Activity & body" block and pick which of its numbers to show. Saved in diet_settings, so
 * the phone app shows the same layout.
 */
import { useState, type ReactElement } from 'react';
import {
  ACTIVITY_FIELDS, activityBlock, DEFAULT_ACTIVITY_FIELDS, effectiveLayout, isBuiltIn, MAX_BLOCKS, MAX_LABEL, newSectionId, PRESETS,
} from '../lib/diaryLayout';
import { MEAL_LABEL } from '../lib/labels';
import { errorMessage } from '../lib/logger';
import { MEAL_TYPES, type ActivityField, type DiaryBlock } from '../lib/types';
import { useApp } from '../state/app';
import { Icon } from './Icon';
import { Modal, Spinner } from './ui';

export function DiaryLayoutModal({ onClose }: { onClose: () => void }): ReactElement {
  const { settings, saveSettings, settingsStored, toast, bump } = useApp();
  const [blocks, setBlocks] = useState<DiaryBlock[]>(() => effectiveLayout(settings.diary_layout, settings.energy_mode).map((b) => ({ ...b, fields: b.fields ? [...b.fields] : undefined })));
  const [newName, setNewName] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const update = (i: number, patch: Partial<DiaryBlock>): void => setBlocks((bs) => bs.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const move = (i: number, d: -1 | 1): void => setBlocks((bs) => {
    const j: number = i + d;
    if (j < 0 || j >= bs.length) return bs;
    const next = [...bs];
    [next[i], next[j]] = [next[j]!, next[i]!];
    return next;
  });
  const remove = (i: number): void => setBlocks((bs) => bs.filter((_, j) => j !== i));
  const hasActivity: boolean = blocks.some((b) => b.kind === 'activity');
  const missingBuiltIns = MEAL_TYPES.filter((m) => !blocks.some((b) => b.id === m));

  const addCustom = (): void => {
    const label: string = newName.trim().slice(0, MAX_LABEL);
    if (label === '') { setError('Give the new section a name, e.g. Drinks or Vitamins.'); return; }
    if (blocks.length >= MAX_BLOCKS) { setError(`Up to ${MAX_BLOCKS} sections.`); return; }
    setBlocks((bs) => [...bs, { id: newSectionId(label, bs), kind: 'food', label }]);
    setNewName('');
    setError(null);
  };

  const toggleField = (i: number, f: ActivityField): void => {
    const cur: ActivityField[] = blocks[i]?.fields ?? [...DEFAULT_ACTIVITY_FIELDS];
    update(i, { fields: cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f] });
  };

  const save = async (layout: DiaryBlock[]): Promise<void> => {
    setError(null);
    if (layout.length > 0 && !layout.some((b) => b.kind === 'food' && !b.hidden)) { setError('Keep at least one visible food section.'); return; }
    if (layout.some((b) => b.label.trim() === '')) { setError('Every section needs a name.'); return; }
    setBusy(true);
    try {
      await saveSettings({ diary_layout: layout.map((b) => ({ ...b, label: b.label.trim() })) });
      toast('Diary layout saved');
      bump();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Customize your diary" onClose={onClose} width={680} footer={
      <>
        <button type="button" className="btn btn-ghost" style={{ marginRight: 'auto' }} onClick={() => void save([])} disabled={busy}>Reset to default</button>
        <button type="button" className="btn" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={() => void save(blocks)} disabled={busy}>{busy ? <Spinner /> : <Icon name="check" size={18} />} Save layout</button>
      </>
    }>
      <div className="stack" style={{ gap: 14 }}>
        <div className="row" style={{ gap: 8 }}>
          <span className="faint" style={{ fontSize: 13, fontWeight: 700 }}>Start from:</span>
          {PRESETS.map((p) => <button key={p.name} type="button" className="btn btn-sm btn-ghost" onClick={() => setBlocks(p.blocks())}>{p.name}</button>)}
        </div>

        <div className="layout-list">
          {blocks.map((b, i) => (
            <div key={b.id} className={`layout-row ${b.hidden ? 'hidden' : ''}`}>
              <div className="layout-move">
                <button type="button" className="icon-btn" aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)}><Icon name="arrowUp" size={16} /></button>
                <button type="button" className="icon-btn" aria-label="Move down" disabled={i === blocks.length - 1} onClick={() => move(i, 1)}><Icon name="arrowDown" size={16} /></button>
              </div>
              <div className="layout-main">
                <input className="input" value={b.label} maxLength={MAX_LABEL} onChange={(e) => update(i, { label: e.target.value })} aria-label="Section name" />
                <small className="faint">
                  {b.kind === 'activity' ? 'Watch and body numbers for the day' : isBuiltIn(b.id) ? `Built-in: ${MEAL_LABEL[b.id]}` : 'Your own section'}
                  {b.hidden ? ' · hidden unless it has entries' : ''}
                </small>
                {b.kind === 'activity' && (
                  <div className="field-checks">
                    {ACTIVITY_FIELDS.map((f) => (
                      <label key={f.key} className="check"><input type="checkbox" checked={(b.fields ?? DEFAULT_ACTIVITY_FIELDS).includes(f.key)} onChange={() => toggleField(i, f.key)} /> {f.label}</label>
                    ))}
                  </div>
                )}
              </div>
              <div className="layout-actions">
                {b.kind === 'food' && (
                  <button type="button" className="icon-btn" aria-label={b.hidden ? 'Show' : 'Hide'} title={b.hidden ? 'Show' : 'Hide when empty'} onClick={() => update(i, { hidden: !b.hidden })}><Icon name={b.hidden ? 'eyeOff' : 'check'} size={16} /></button>
                )}
                <button type="button" className="icon-btn" aria-label="Remove" title="Remove" onClick={() => remove(i)}><Icon name="trash" size={16} /></button>
              </div>
            </div>
          ))}
        </div>

        <div className="stack" style={{ gap: 8 }}>
          <div className="input-row">
            <input className="input" value={newName} maxLength={MAX_LABEL} placeholder="New section, e.g. Drinks, Vitamins & supplements, Late snack" onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addCustom(); }} aria-label="New section name" />
            <button type="button" className="btn" onClick={addCustom}><Icon name="plus" size={16} /> Add</button>
          </div>
          <div className="row" style={{ gap: 6 }}>
            {!hasActivity && <button type="button" className="btn btn-sm btn-ghost" onClick={() => setBlocks((bs) => [activityBlock(), ...bs])}><Icon name="watch" size={15} /> Add the Activity &amp; body block</button>}
            {missingBuiltIns.map((m) => <button key={m} type="button" className="btn btn-sm btn-ghost" onClick={() => setBlocks((bs) => [...bs, { id: m, kind: 'food', label: MEAL_LABEL[m] }])}><Icon name="plus" size={15} /> {MEAL_LABEL[m]}</button>)}
          </div>
        </div>

        <p className="faint" style={{ margin: 0, fontSize: 12.5, fontWeight: 600 }}>
          Removing a section never deletes food: anything logged in it shows under Other. The phone app uses the same layout.
          {settingsStored === 'device' ? ' Until the database update runs, this layout is kept in this browser only.' : ''}
        </p>
        {error !== null && <div className="note err">{error}</div>}
      </div>
    </Modal>
  );
}
